import { Add, Delete, Edit, UploadFile } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { navigate } from '@reach/router';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import apiClient from '../api/client';
import AdvancedFilters from '../components/AdvancedFilters';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import AsyncTableSearch from '../components/AsyncTableSearch';
import ExcelImportDialog from '../components/ExcelImportDialog';
import Layout from '../components/Layout';
import StyledTableContainer from '../components/StyledTableContainer';
import { useAuth } from '../contexts/AuthContext';
import { useDeals, useUI } from '../store';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';

const DealStatusChip = ({ status }) => {
  const statusMap = {
    'new': { label: 'Новая', color: 'primary' },
    'in_progress': { label: 'В процессе', color: 'warning' },
    'completed': { label: 'Завершена', color: 'success' },
    'canceled': { label: 'Отменена', color: 'error' }
  };

  const statusInfo = statusMap[status] || { label: status, color: 'default' };

  return (
    <Chip label={statusInfo.label} color={statusInfo.color} />
  );
};

const FILTER_FIELDS = [
  { value: 'client', label: 'Клиент' },
  { value: 'product', label: 'Продукт' },
  { value: 'quantity', label: 'Количество' },
  { value: 'amount', label: 'Сумма' },
  { value: 'status', label: 'Статус' },
  { value: 'title', label: 'Название' },
];

const SEARCH_FIELDS = ['title'];

const DealsPage = () => {
  const { user, loading: authLoading } = useAuth();

  // ✅ useDeals с useShallow - правильный способ без бесконечных циклов
  const {
    deals,
    loading,
    error,
    pagination,
    fetchDeals,
    createDeal,
    updateDeal,
    deleteDeal,
    setDealsPage,
    setDealsFilters
  } = useDeals();

  const { startEditing, stopEditing, addNotification } = useUI();

  // ✅ Подключение к SSE для real-time обновлений
  useRealtimeSSE();

  const [filteredDeals, setFilteredDeals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState([]);

  // ✅ Обработчик поиска с автоматическим сбросом пагинации
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
    setDealsFilters({ search: query });
  }, [setDealsFilters]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [productNameById, setProductNameById] = useState({});
  // Состояние для пагинации поиска
  const [searchActive, setSearchActive] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchPagination, setSearchPagination] = useState({ page: 1, pages: 1, total: 0, perPage: 20 });
  const [dealCacheById, setDealCacheById] = useState({});
  const [failedProductIds, setFailedProductIds] = useState(new Set()); // Кэш для несуществующих продуктов
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    product_id: '',
    quantity: '1',
    title: '',
    amount: '',
    status: 'new',
    pipelineId: '',
    stageId: '',
    probability: 0,
    expectedCloseDate: ''
  });
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (user) {
      fetchClients();
      fetchProducts();
      fetchDeals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  useEffect(() => {
    if (deals.length > 0) {
      let filtered = deals;

      // ✅ Поиск теперь на сервере через setDealsFilters, локальная фильтрация не нужна

      // Применяем расширенные фильтры (локально)
      if (advancedFilters.length > 0) {
        filtered = filtered.filter(deal => {
          return advancedFilters.every(filter => {
            let value;
            if (filter.field === 'client') {
              value = getClientName(deal.client_id);
            } else if (filter.field === 'product') {
              value = getProductName(deal.product_id);
            } else {
              value = deal[filter.field];
            }

            if (value === null || value === undefined) return false;

            const stringValue = String(value).toLowerCase();
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
              case 'contains':
                return stringValue.includes(filterValue);
              case 'equals':
                return stringValue === filterValue;
              case 'startsWith':
                return stringValue.startsWith(filterValue);
              case 'endsWith':
                return stringValue.endsWith(filterValue);
              case 'greaterThan':
                return Number(value) > Number(filter.value);
              case 'lessThan':
                return Number(value) < Number(filter.value);
              default:
                return true;
            }
          });
        });
      }

      setFilteredDeals(prev => {
          if (
            prev.length === filtered.length &&
            prev.every((x, i) =>
              (x.id || x._id) === (filtered[i]?.id || filtered[i]?._id)
            )
          ) {
            return prev; // ничего не изменилось → не делаем rerender → нет цикла
          }
          return filtered;
        });
    }
  }, [deals, clients, products, advancedFilters]); // ✅ Убрали searchQuery - он теперь на сервере

  const fetchClients = async () => {
    try {
      const response = await apiClient.get('/clients');
      setClients(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке клиентов:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/products');
      setProducts(response.data);
      // Инициализируем кэш имён
      const map = {};
      (response.data || []).forEach((p) => {
        if (p?.id) map[p.id.toString()] = p.name;
      });
      setProductNameById(map);
    } catch (err) {
      console.error('Ошибка при загрузке продуктов:', err);
    }
  };

  const fetchProductById = async (id) => {
    if (!id) return null;

    const idStr = id.toString();

    // Проверяем кэш failed продуктов - не делаем повторных запросов
    if (failedProductIds.has(idStr)) {
      console.log(`[Product] Skipping failed product ${idStr}`);
      return null;
    }

    try {
      const resp = await apiClient.get(`/products/${id}`);
      const prod = resp.data || null;
      if (prod?.id) {
        setProductNameById(prev => ({ ...prev, [prod.id.toString()]: prod.name }));
        // Синхронизируем локальный список products (merge по id)
        setProducts(prev => {
          const key = prod.id.toString();
          const exists = prev.some(p => p.id?.toString() === key);
          if (exists) {
            return prev.map(p => (p.id?.toString() === key ? { ...p, ...prod } : p));
          }
          return [...prev, prod];
        });
      }
      return prod;
    } catch (e) {
      // Логируем ошибки и добавляем в кэш failed
      if (e.response?.status === 404) {
        console.warn(`[Product] Product ${idStr} not found (404)`);
        setProductNameById(prev => ({ ...prev, [idStr]: 'Удален' }));
      } else if (e.response?.status === 403) {
        console.error(`[Product] Access denied to product ${idStr} (403)`);
        setProductNameById(prev => ({ ...prev, [idStr]: 'Нет доступа' }));
      } else {
        console.error(`[Product] Error fetching product ${idStr}:`, e.message);
        setProductNameById(prev => ({ ...prev, [idStr]: 'Ошибка' }));
      }

      // Добавляем в кэш failed чтобы не делать повторные запросы
      setFailedProductIds(prev => new Set([...prev, idStr]));
      return null;
    }
  };

  const recalculateAmount = async (productId, qty) => {
    const quantity = parseInt(qty) || 0;
    let prod = products.find(p => p.id?.toString() === productId?.toString());
    if (!prod) {
      prod = await fetchProductById(productId);
    }
    const price = Number(prod?.price || 0);
    const amountStr = (price * quantity).toString();
    setFormData(prev => ({ ...prev, amount: amountStr }));
  };

  // Подгружаем имена продуктов, которых нет в локальном списке
  useEffect(() => {
    const missingIds = (filteredDeals || [])
      .map(d => d.product_id?.toString())
      .filter(Boolean)
      .filter(id => !productNameById[id]);
    if (missingIds.length === 0) return;
    (async () => {
      for (const id of Array.from(new Set(missingIds))) {
        await fetchProductById(id);
      }
    })();
  }, [filteredDeals, productNameById]);

  const handleOpenDialog = (deal = null) => {
    if (deal) {
      const dealId = deal.id || deal._id;
      startEditing(dealId);

      // Если пришёл объект из поиска, положим его в кэш по id
      if (dealId) {
        setDealCacheById(prev => ({ ...prev, [dealId]: deal }));
      }
      const src = dealCacheById[dealId] || deal;
      setEditingDeal(src);
      setFormData({
        client_id: src.client_id?.toString() || '',
        product_id: src.product_id?.toString() || '',
        quantity: (src.quantity ?? 1).toString(),
        title: src.title || '',
        amount: (src.amount ?? 0).toString(),
        status: src.status || 'new'
      });
    } else {
      setEditingDeal(null);
      const defaultProduct = products[0] || {};
      const unit = defaultProduct.price || 0;
      setFormData({
        client_id: clients[0]?.id.toString() || '',
        product_id: defaultProduct.id?.toString() || '',
        quantity: '1',
        title: '',
        amount: unit.toString(),
        status: 'new'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (editingDeal) {
      stopEditing(editingDeal.id || editingDeal._id);
    }
    setOpenDialog(false);
    setEditingDeal(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'product_id') {
      recalculateAmount(value, formData.quantity);
    }
    if (name === 'quantity') {
      recalculateAmount(formData.product_id, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Форматирование данных для API
    const dealData = {
      ...formData,
      amount: parseFloat(formData.amount),
      quantity: parseInt(formData.quantity)
    };

    try {
      if (editingDeal) {
        await updateDeal(editingDeal.id || editingDeal._id, dealData);
        addNotification('success', 'Сделка обновлена');
        stopEditing(editingDeal.id || editingDeal._id);
      } else {
        await createDeal(dealData);
        addNotification('success', 'Сделка создана');
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Ошибка при сохранении сделки:', err);
      addNotification('error', err.response?.data?.message || 'Не удалось сохранить данные сделки');
      if (editingDeal) {
        stopEditing(editingDeal.id || editingDeal._id);
      }
    }
  };

  const handleDelete = async (dealId) => {
    if (window.confirm('Вы уверены, что хотите удалить эту сделку?')) {
      try {
        await deleteDeal(dealId);
        addNotification('success', 'Сделка удалена');
      } catch (err) {
        console.error('Ошибка при удалении сделки:', err);
        addNotification('error', err.response?.data?.message || 'Не удалось удалить сделку');
      }
    }
  };

  const getClientName = (clientId) => {
    if (!clientId) return 'Неизвестный клиент';
    const client = clients.find(c => c.id?.toString() === clientId.toString());
    return client ? client.name : 'Неизвестный клиент';
  };

  const getProductName = (productId) => {
    if (!productId) return '—';
    const key = productId.toString();
    if (productNameById[key]) return productNameById[key];
    const product = products.find(p => p.id?.toString() === key);
    return product ? product.name : 'Загрузка…';
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => {
    setAdvancedFilters([]);
  };

  const handlePageChange = (event, value) => {
    if (searchActive) {
      // При активном поиске меняем страницу поиска
      setSearchPage(value);
    } else {
      // Обычная пагинация
      setDealsPage(value);
    }
  };

  // Callback для результатов поиска
  const handleSearchResults = useCallback((items) => {
    setFilteredDeals(items);
  }, []);

  // Callback для изменения пагинации поиска
  const handleSearchPaginationChange = useCallback((paginationInfo) => {
    setSearchPagination(paginationInfo);
  }, []);

  // Callback для изменения активности поиска
  const handleSearchActiveChange = useCallback((active) => {
    setSearchActive(active);
    if (!active) {
      setFilteredDeals(deals);
      setSearchPage(1);
      setSearchPagination({ page: 1, pages: 1, total: 0, perPage: 20 });
    }
  }, [deals]);

  // Виртуализированный рендеринг строк таблицы
  const Row = ({ index, style }) => {
    const deal = filteredDeals[index];
    const isOptimistic = deal._optimistic;

    return (
      <TableRow
        key={deal.id || deal._id}
        style={{
          ...style,
          opacity: isOptimistic ? 0.6 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
        sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.1)' } }}
      >
        <TableCell sx={{ color: 'white' }}>
          {deal.title}
          {isOptimistic && (
            <Chip
              label="..."
              size="small"
              sx={{ ml: 1, backgroundColor: '#FFC107', color: 'white', height: 18 }}
            />
          )}
        </TableCell>
        <TableCell sx={{ color: 'white' }}>{getProductName(deal.product_id)}</TableCell>
        <TableCell sx={{ color: 'white' }}>{deal.quantity}</TableCell>
        <TableCell sx={{ color: 'white' }}>{getClientName(deal.client_id)}</TableCell>
        <TableCell sx={{ color: 'white' }}>{deal.amount ? deal.amount.toLocaleString() : '0'} ₸</TableCell>
        <TableCell>
          <DealStatusChip status={deal.status || 'new'} />
        </TableCell>
        <TableCell sx={{ position: 'sticky', right: 0, background: 'rgba(30,30,30,0.7)', zIndex: 1 }}>
          <IconButton onClick={async () => {
            const dealId = deal.id || deal._id;
            // Если элемента нет в локальном кэше — дотянем по API перед открытием
            if (dealId && !dealCacheById[dealId]) {
              try {
                const resp = await apiClient.get(`/deals/${dealId}`);
                const full = resp.data || deal;
                setDealCacheById(prev => ({ ...prev, [dealId]: full }));
                handleOpenDialog(full);
                return;
              } catch (e) {
                // fallback к тому что есть
              }
            }
            handleOpenDialog(deal);
          }}>
            <Edit />
          </IconButton>
          <IconButton onClick={() => handleDelete(deal.id || deal._id)}>
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  const useVirtualization = filteredDeals.length > 100;

  return (
    <Layout>
      <Box sx={{
        p: 3,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
      }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: { sm: 'space-between' }, gap: { xs: 2, sm: 0 } }}>
          <Typography variant="h4" component="h1" sx={{ mr: { sm: 'auto' } }}>
            Сделки
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' }, gap: { xs: 1, sm: 2 } }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Добавить сделку
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={() => setImportOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Импорт из Excel
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <AsyncTableSearch
            value={searchQuery}
            onChange={handleSearchChange}
            onResults={handleSearchResults}
            onActiveChange={handleSearchActiveChange}
            onPaginationChange={handleSearchPaginationChange}
            onLoadingChange={setSearchLoading}
            placeholder="Поиск сделок..."
            searchDoc="deals"
            searchFields={SEARCH_FIELDS}
            page={searchPage}
            perPage={pagination.perPage}
            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
          />
          <AdvancedFilters
            filters={advancedFilters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            fields={FILTER_FIELDS}
          />
        </Box>

        {clients.length === 0 && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}>
            Для добавления сделок сначала создайте клиента в разделе "Клиенты"
          </Paper>
        )}

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            {error}
          </Paper>
        )}
        {searchLoading && <LinearProgress sx={{ mb: 1 }} />}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'white' }}>Название</TableCell>
                <TableCell sx={{ color: 'white' }}>Продукт</TableCell>
                <TableCell sx={{ color: 'white' }}>Кол-во</TableCell>
                <TableCell sx={{ color: 'white' }}>Клиент</TableCell>
                <TableCell sx={{ color: 'white' }}>Сумма</TableCell>
                <TableCell sx={{ color: 'white' }}>Статус</TableCell>
                <TableCell sx={{ color: 'white', position: 'sticky', right: 0, background: 'rgba(30,30,30,0.8)', zIndex: 2 }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading || searchLoading ? (
                <TableRow>
                  <TableCell sx={{ color: 'white' }} colSpan={7} align="center">Загрузка...</TableCell>
                </TableRow>
              ) : filteredDeals.length === 0 ? (
                <TableRow>
                  <TableCell sx={{ color: 'white' }} colSpan={7} align="center">
                    {searchQuery ? 'Сделки не найдены' : 'Сделки не найдены'}
                  </TableCell>
                </TableRow>
              ) : useVirtualization ? (
                <List
                  height={600}
                  itemCount={filteredDeals.length}
                  itemSize={60}
                  width="100%"
                >
                  {Row}
                </List>
              ) : (
                filteredDeals.map((deal, index) => <Row key={deal.id || deal._id} index={index} style={{}} />)
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
        {/* Пагинация: разные данные для поиска и обычного режима */}
        {(() => {
          const currentPage = searchActive ? searchPagination.page : pagination.page;
          const currentTotalPages = searchActive ? searchPagination.pages : pagination.totalPages;
          const currentTotal = searchActive ? searchPagination.total : pagination.total;

          if (currentTotalPages <= 1 && !searchActive) return null;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
              {searchActive && (
                <Typography variant="body2" color="text.secondary">
                  Найдено: {currentTotal}
                </Typography>
              )}
              {currentTotalPages > 1 && (
                <Pagination 
                  color="primary" 
                  count={currentTotalPages} 
                  page={currentPage} 
                  onChange={handlePageChange} 
                />
              )}
            </Box>
          );
        })()}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDeal ? 'Редактировать сделку' : 'Добавить сделку'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Название"
              name="title"
              value={formData.title}
              onChange={handleChange}
              margin="normal"
              required
            />
            <AsyncSearchSelect
              name="client_id"
              label="Клиент"
              value={formData.client_id}
              onChange={handleChange}
              searchDoc="clients"
              searchField="name"
              fallbackOptions={clients}
              getOptionId={(o) => o.id?.toString?.()}
              getOptionLabel={(o) => o.name || ''}
            />
            <AsyncSearchSelect
              name="product_id"
              label="Продукт"
              value={formData.product_id}
              onChange={handleChange}
              searchDoc="products"
              searchField="name"
              fallbackOptions={products}
              getOptionId={(o) => o.id?.toString?.()}
              getOptionLabel={(o) => o.name || ''}
            />
            <TextField
              fullWidth
              label="Количество"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                inputProps: {
                  min: 1,
                  max: products.find(p => p.id?.toString() === formData.product_id)?.stock || 1,
                  step: "1"
                }
              }}
            />
            <TextField
              fullWidth
              label="Сумма"
              name="amount"
              type="number"
              value={formData.amount}
              margin="normal"
              InputProps={{
                readOnly: true,
                inputProps: { min: 0, step: "0.01" }
              }}
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="status-label">Статус</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Статус"
              >
                <MenuItem value="new">Новая</MenuItem>
                <MenuItem value="in_progress">В процессе</MenuItem>
                <MenuItem value="completed">Завершена</MenuItem>
                <MenuItem value="canceled">Отменена</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>
      <ExcelImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/deals/import"
        onSuccess={fetchDeals}
      />
    </Layout>
  );
};

export default DealsPage;